const DAYS_ORDER = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

const isValidDay = (value: any): boolean => DAYS_ORDER.includes(value);

const toHHmm = (value: any): string => {
  if (!value) return '';

  if (typeof value === 'string') {
    if (/^\d{2}:\d{2}$/.test(value)) return value;
    const match = value.match(/(\d{2}:\d{2})/);
    return match?.[1] ?? '';
  }

  if (typeof value === 'object' && typeof value.date === 'string') {
    const match = value.date.match(/(\d{2}:\d{2})/);
    return match?.[1] ?? '';
  }

  return '';
};

const sortByStartTime = (slots: any[]) =>
  [...slots].sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));

const normalizeAvailabilityFromDays = (days: any[]): any[] => {
  return (days || [])
    .filter((day) => isValidDay(day?.dayOfWeek))
    .map((day) => ({
      dayOfWeek: day.dayOfWeek,
      slots: sortByStartTime(
        (day.slots || [])
          .map((slot: any) => ({
            startTime: toHHmm(slot?.startTime),
            endTime: toHHmm(slot?.endTime),
            period: slot?.period ?? 'mixed',
          }))
          .filter((slot: any) => slot.startTime && slot.endTime)
      ),
    }))
    .filter((day) => day.slots.length > 0)
    .sort((a, b) => DAYS_ORDER.indexOf(a.dayOfWeek) - DAYS_ORDER.indexOf(b.dayOfWeek));
};

const normalizeAvailabilityFromFlatSlots = (availabilitySlots: any[]): any[] => {
  const byDay: Record<string, any[]> = {};

  (availabilitySlots || []).forEach((slot) => {
    const dayOfWeek = slot?.dayOfWeek;
    if (!isValidDay(dayOfWeek)) return;

    const normalizedSlot = {
      startTime: toHHmm(slot?.startTime),
      endTime: toHHmm(slot?.endTime),
      period: slot?.period ?? 'mixed',
    };

    if (!normalizedSlot.startTime || !normalizedSlot.endTime) return;

    if (!byDay[dayOfWeek]) {
      byDay[dayOfWeek] = [];
    }
    byDay[dayOfWeek].push(normalizedSlot);
  });

  return DAYS_ORDER
    .filter((day) => byDay[day]?.length)
    .map((day) => ({
      dayOfWeek: day,
      slots: sortByStartTime(byDay[day]),
    }));
};

export const normalizeProvidingCorporationAvailability = (pc: any): any[] => {
  if (Array.isArray(pc?.availability) && pc.availability.length > 0) {
    return normalizeAvailabilityFromDays(pc.availability);
  }

  if (Array.isArray(pc?.availabilitySlots) && pc.availabilitySlots.length > 0) {
    return normalizeAvailabilityFromFlatSlots(pc.availabilitySlots);
  }

  return [];
};

export const mapProvidingCorporationsForForm = (data: any): any[] => {
  if (Array.isArray(data?.providingCorporations) && data.providingCorporations.length > 0) {
    return data.providingCorporations.map((pc: any) => ({
      tutorProvidingCorporationId: pc.id ?? '',
      providingCorporationId: pc.providingCorporation?.id ?? '',
      platformUser: pc.platformUser ?? '',
      platformPassword: pc.platformPassword ?? '',
      collaborationSignature: typeof pc.collaborationSignature === 'boolean'
        ? pc.collaborationSignature
        : !!data?.collaborationSignature,
      availability: normalizeProvidingCorporationAvailability(pc),
    }));
  }

  return [{
    tutorProvidingCorporationId: '',
    providingCorporationId: '',
    platformUser: data?.platformUser ?? '',
    platformPassword: data?.platformPassword ?? '',
    collaborationSignature: !!data?.collaborationSignature,
    availability: [],
  }];
};
